import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withFormik } from "formik";
import * as Yup from "yup";
import get from "lodash/get";
import isArray from "lodash/isArray";
import objectToFormData from "object-to-formdata";
import PropTypes from "prop-types";
import Actions from "../actions";
import useAlert from "@/hooks/useAlert";

const Main = ({
  children,
  handleSubmit,
  submitForm,
  values,
  isSubmitting,
  setFieldValue,
  setFieldError,
  setFieldTouched,
  errors,
}) => (
  <form onSubmit={handleSubmit}>
    {children({
      handleSubmit,
      submitForm,
      values,
      isSubmitting,
      setFieldValue,
      setFieldError,
      setFieldTouched,
      errors,
    })}
  </form>
);

Main.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  entity: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  url: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  method: PropTypes.oneOf(["get", "post", "put", "delete"]).isRequired,
  primaryKey: PropTypes.string,
  fields: PropTypes.array.isRequired,
  appendData: PropTypes.bool,
  prependData: PropTypes.bool,
  updateData: PropTypes.bool,
  deleteData: PropTypes.bool,
  normalizeData: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  sendAsFormData: PropTypes.bool,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  isMulti: PropTypes.bool,
};

Main.defaultProps = {
  primaryKey: "id",
  appendData: false,
  prependData: false,
  updateData: false,
  deleteData: false,
  normalizeData: "",
  sendAsFormData: true,
  isMulti: false,
  onSuccess: () => {},
  onError: () => {},
};

const EnhacedForm = withFormik({
  enableReinitialize: true,
  validationSchema: ({ fields }) => {
    if (!isArray(fields)) {
      return Yup.object().shape({});
    }

    let validationFields = {};

    fields.forEach((field) => {
      let validationField;

      switch (field.type) {
        case "string":
          validationField = Yup.string().typeError("Must be a string");
          break;
        case "object":
          validationField = Yup.object();
          break;
        case "number":
          validationField = Yup.number().typeError("Must be a number");
          break;
        case "array":
          validationField = Yup.array();
          break;
        case "boolean":
          validationField = Yup.boolean();
          break;
        case "date":
          validationField = Yup.date();
          break;
        default:
          
          
          
          
          
          
          validationField = Yup.mixed();
      }

      if (field.required) {
        if (field.type) {
          validationField = validationField.required("Необходимо заполнить");
        } else {
          validationField = validationField.test(
            "required",
            "Необходимо заполнить",
            (value) => {
              if (value === null || value === undefined) return false;
              if (typeof value === "string") return value.trim().length > 0;
              if (Array.isArray(value)) return value.length > 0;
              return true;
            }
          );
        }
      }

      if (field.min) {
        validationField = validationField.min(field.min, "Too Short!");
      }

      if (field.max) {
        validationField = validationField.max(field.max, "Too Long!");
      }

      validationField = validationField.nullable();

      validationFields[field.name] = validationField;
    });

    return Yup.object().shape(validationFields);
  },
  mapPropsToValues: ({ fields }) => {
    return isArray(fields)
      ? fields.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.name]: curr.isAbsolute ? curr.value : get(curr, "value", ""),
          }),
          {}
        )
      : {};
  },
  handleSubmit: (
    values,
    { props, setFieldError, setSubmitting, resetForm }
  ) => {
    let {
      id,
      entity,
      name,
      url,
      params,
      method,
      primaryKey,
      isMulti,
      fields,
      appendData,
      prependData,
      updateData,
      deleteData,
      normalizeData,
      sendAsFormData,
      onSuccess = () => {},
      onError = () => {},
      FormAction,
      selfErrorMessage,
    } = props;

    values = { ...values };

    if (typeof url === "function") {
      url = url({ ...values });
    }

    fields.forEach((field) => {
      if (field.hasOwnProperty("onSubmitValue")) {
        if (typeof field.onSubmitValue === "function") {
          if (field.hasOwnProperty("onSubmitKey")) {
            values[field.onSubmitKey] = field.onSubmitValue(
              values[field.name],
              values
            );
            delete values[field.name];
          } else {
            values[field.name] = field.onSubmitValue(
              values[field.name],
              values
            );
          }
        }
      }
      if (field.hasOwnProperty("disabled")) {
        delete values[field.name];
      }
    });

    if (sendAsFormData) {
      values = objectToFormData(values);
      
    }

    FormAction({
      id,
      entity,
      name,
      url,
      params,
      method,
      primaryKey,
      values,
      isMulti,
      appendData,
      prependData,
      updateData,
      deleteData,
      normalizeData,
      cb: {
        success: (data) => {
          try {
            onSuccess(data, resetForm);
          } catch (err) {
            console.error("onSuccess callback error:", err);
          }
        },
        error: (errors = []) => {
          try {
            if (!selfErrorMessage) {
              new useAlert().showAlert("", { type: "danger" });
            }

            if (errors instanceof Array) {
              errors.forEach(({ field, message }) => {
                if (field) setFieldError(field, message);
              });
            } else if (
              errors &&
              errors.errors &&
              typeof errors.errors === "object"
            ) {
              Object.keys(errors.errors).forEach((field) => {
                const errorArray = errors.errors[field];
                const error = Array.isArray(errorArray)
                  ? errorArray[0]
                  : errorArray;
                if (field && error) setFieldError(field, error);
              });
            } else if (errors && typeof errors === "object") {
              Object.keys(errors).forEach((field) => {
                const errorArray = errors[field];
                const error = Array.isArray(errorArray)
                  ? errorArray[0]
                  : errorArray;
                if (field && error) setFieldError(field, error);
              });
            }
          } catch (err) {
            console.error("Error field setting error:", err);
          }

          try {
            onError(errors, setFieldError);
          } catch (err) {
            console.error("onError callback error:", err);
          }
        },
        finally: () => {
          setSubmitting(false);
        },
      },
    });
  },
})(Main);

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      FormAction: Actions.Form.request,
    },
    dispatch
  );

export default connect(null, mapDispatchToProps)(EnhacedForm);
